import React, { useState } from 'react';
import { FiEdit2, FiTrash2, FiUser, FiClock, FiAlertCircle, FiChevronDown, FiChevronUp, FiFolder } from 'react-icons/fi';
import { format, isToday, isPast, startOfDay } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useTask } from '../contexts/TaskContext';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SplitSubtaskButton from './SplitSubtaskButton';
import '../styles/TaskCard.css';

const TaskCard = ({ task, onEdit, onOpen, isDragging = false }) => {
  const { user } = useAuth();
  const { deleteTask, users, setSubtaskCompleted, hasPendingFor } = useTask();
  const [showActions, setShowActions] = useState(false);
  const [showSubtasksModal, setShowSubtasksModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const modalRef = React.useRef(null);

  const isHRUser = user?.role === 'hr';

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: task._id, disabled: isHRUser });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging || isSortableDragging ? 0.5 : 1,
    cursor: isHRUser ? 'default' : undefined,
  };


  // Check ownership - handle both object and string ID formats
  const taskCreatorId = typeof task.createdBy === 'object' ? task.createdBy?._id : task.createdBy;
  const currentUserId = user?._id || user?.id;
  const isOwner = taskCreatorId === currentUserId;

  // Check if user is assigned to this task
  const isAssignee = task.assignees?.some(a => {
    const assigneeId = typeof a === 'object' ? a._id : a;
    return assigneeId === currentUserId;
  }) || (task.assignee && (typeof task.assignee === 'object' ? task.assignee._id : task.assignee) === currentUserId);

  // User can edit/split if they own, are assigned, or are a manager/admin
  // HR can VIEW all tasks but NOT edit them
  const isHR = user?.role === 'hr';
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';
  const canEdit = !isHR && (isOwner || isAssignee || isManagerOrAdmin);
  const canSplitSubtasks = canEdit;
  const canDelete = canEdit;
  const canDrag = !isHR; // HR cannot reorder tasks

  // Build display names for assigned users. Prefer `task.assignedTo` (normalized),
  // otherwise derive from `assignees` or single `assignee`.
  const assignedUsers = (task.assignedTo && task.assignedTo.length > 0)
    ? task.assignedTo
    : (task.assignees?.length > 0
      ? task.assignees.map(u => u.username || u).filter(Boolean)
      : task.assignee?.username
        ? [task.assignee.username]
        : []);
  const assignedUser = assignedUsers.length > 0 ? assignedUsers.join(', ') : null;

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      await deleteTask(task._id);
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 5: return 'critical';
      case 4: return 'high';
      case 3: return 'medium';
      case 2: return 'low';
      case 1: return 'minimal';
      default: return 'medium';
    }
  };

  const getUrgencyLabel = (urgency) => {
    switch (urgency) {
      case 5: return 'Critical';
      case 4: return 'High';
      case 3: return 'Medium';
      case 2: return 'Low';
      case 1: return 'Minimal';
      default: return 'Medium';
    }
  };

  // Get due date status for color coding
  const getDueDateStatus = () => {
    if (!task.deadline) return null;
    const deadlineDate = startOfDay(new Date(task.deadline));
    const today = startOfDay(new Date());

    if (isPast(deadlineDate) && !isToday(deadlineDate)) {
      return 'overdue';
    } else if (isToday(deadlineDate)) {
      return 'due-today';
    } else {
      return 'future';
    }
  };

  const dueDateStatus = getDueDateStatus();
  const isOverdue = dueDateStatus === 'overdue';

  const handleDoubleClick = () => {
    if (!isOwner) return;
    // Prefer explicit onOpen (open full modal). Fall back to onEdit for backwards compatibility.
    if (typeof onOpen === 'function') onOpen(task);
    else if (typeof onEdit === 'function') onEdit(task);
  };

  const handleToggleSubtask = (idx, value) => {
    setSubtaskCompleted(task._id, idx, value);
  };

  // Determine if there are pending local changes for this task
  const isPending = hasPendingFor(task._id);

  // Modal accessibility: focus trap and Escape handling
  React.useEffect(() => {
    if (!showSubtasksModal) return;
    const modalEl = modalRef.current;
    if (modalEl) {
      modalEl.focus();
    }
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setShowSubtasksModal(false);
      }
      if (e.key === 'Tab') {
        // simple focus trap
        const focusables = modalEl?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [showSubtasksModal]);

  // Don't render if task is undefined (during drag overlay)
  if (!task) return null;

  // Determine if task should be collapsible (todo or done status)
  const isCollapsible = task.status === 'todo' || task.status === 'done';
  const isCollapsed = isCollapsible && !isExpanded;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`task-card ${isSortableDragging ? 'dragging' : ''} ${isCollapsed ? 'collapsed' : ''} ${dueDateStatus ? `due-${dueDateStatus}` : ''}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onDoubleClick={handleDoubleClick}
    >
      <div className="task-card-header">
        <div className="task-tags">
          <div
            className={`task-urgency urgency-${getUrgencyColor(task.urgency)}`}
          >
            {getUrgencyLabel(task.urgency)}
          </div>
          {isOverdue && (
            <div className="task-delay-tag">
              Delayed
            </div>
          )}
          {task.projectId && (
            <div className="task-project-tag" style={{ borderColor: task.projectId.color || '#4f46e5' }}>
              <FiFolder size={10} style={{ color: task.projectId.color || '#4f46e5' }} />
              <span>{task.projectId.name || 'Project'}</span>
            </div>
          )}
          {task.reviews && task.reviews.length > 0 && (
            (() => {
              const latest = task.reviews[task.reviews.length - 1];
              const reviewerName = latest.reviewerId?.username || 'Reviewer';
              if (latest.status === 'approved') return <div className="task-review-tag" style={{ background: '#d1fae5', color: '#065f46', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>Passed by {reviewerName}</div>;
              if (latest.status === 'rejected') return <div className="task-review-tag" style={{ background: '#fee2e2', color: '#991b1b', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>Failed by {reviewerName}</div>;
              if (latest.status === 'changes-requested') return <div className="task-review-tag" style={{ background: '#fef3c7', color: '#92400e', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>Need Changes by {reviewerName}</div>;
              return null;
            })()
          )}
        </div>
        <div className="task-header-actions">
          {showActions && isCollapsible && (
            <button
              className="action-btn expand-btn"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
            </button>
          )}
          {showActions && canEdit && (
            <div className="task-actions">
              <button
                className="action-btn edit-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(task);
                }}
                title="Edit task"
              >
                <FiEdit2 />
              </button>
              {isOwner && (
                <button
                  className="action-btn delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  title="Delete task"
                >
                  <FiTrash2 />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Collapsed view: title, assignee, deadline only */}
      {isCollapsed ? (
        <div className="task-collapsed-content">
          <h3 className="task-title">{task.title}</h3>
          <div className="task-collapsed-info">
            {assignedUser && (
              <span className="collapsed-assignee">
                <FiUser size={12} /> {assignedUser}
              </span>
            )}
            {task.deadline && (
              <span className="collapsed-deadline">
                <FiClock size={12} /> {format(new Date(task.deadline), 'MMM dd')}
              </span>
            )}
          </div>
        </div>
      ) : (
        /* Expanded view: full content */
        <>
          <h3 className="task-title">{task.title}</h3>
          <p className="task-description">{task.description}</p>
          <h4>{assignedUsers.length > 0 ? `Assigned to: ${assignedUsers.join(', ')}` : 'Unassigned'}</h4>
          {task.subtasks && task.subtasks.length > 0 && (
            <div className="task-subtasks">
              <div className="subtasks-summary">
                <span>
                  {task.subtasks.filter(s => s.completed).length} / {task.subtasks.length} subtasks
                </span>
                {isPending && (
                  <span className="pending-indicator" title="Unsynced changes">●</span>
                )}
              </div>
              <div className="subtasks-list">
                {task.subtasks.slice(0, 3).map((subtask, idx) => (
                  <div key={idx} className="subtask-item subtask-item-with-actions">
                    <div className="subtask-content">
                      <input
                        type="checkbox"
                        className="radio-toggle"
                        checked={!!(subtask && subtask.completed)}
                        onChange={(e) => handleToggleSubtask(idx, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className={subtask.completed ? 'completed' : ''}>
                        {typeof subtask === 'string' ? subtask : subtask.title}
                      </span>
                    </div>
                    {canSplitSubtasks && (
                      <div className="subtask-actions">
                        <SplitSubtaskButton
                          parentTask={task}
                          subtaskIndex={idx}
                          subtask={subtask}
                        />
                      </div>
                    )}
                  </div>
                ))}
                {task.subtasks.length > 3 && (
                  <button className="subtasks-more" onClick={(e) => { e.stopPropagation(); setShowSubtasksModal(true); }}>
                    +{task.subtasks.length - 3} more
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="task-footer">
            {task.deadline && (
              <div className="task-deadline">
                <FiClock size={14} />
                <span>{format(new Date(task.deadline), 'MMM dd, yyyy')}</span>
              </div>
            )}
            {assignedUser && (
              <div className="task-assigned">
                <FiUser size={14} />
                <span>{assignedUser}</span>
              </div>
            )}
            {!canEdit && (
              <div className="task-owner-info" title="You can only edit tasks you created or are assigned to">
                <FiAlertCircle size={14} />
                <span>Read-only</span>
              </div>
            )}
          </div>
        </>
      )}
      {showSubtasksModal && (
        <div className="subtasks-modal-overlay" aria-modal="true" role="dialog">
          <div className="subtasks-modal" ref={modalRef} tabIndex={-1}>
            <div className="subtasks-modal-header">
              <h4>Subtasks</h4>
              <button className="close-btn" onClick={() => setShowSubtasksModal(false)}>×</button>
            </div>
            <div className="subtasks-modal-body">
              {task.subtasks.map((subtask, idx) => (
                <div key={idx} className="subtask-item subtask-item-with-actions">
                  <div className="subtask-content">
                    <input
                      type="checkbox"
                      className="radio-toggle"
                      checked={!!(subtask && subtask.completed)}
                      onChange={(e) => handleToggleSubtask(idx, e.target.checked)}
                    />
                    <span className={subtask.completed ? 'completed' : ''}>
                      {typeof subtask === 'string' ? subtask : subtask.title}
                    </span>
                  </div>
                  {canSplitSubtasks && (
                    <div className="subtask-actions">
                      <SplitSubtaskButton
                        parentTask={task}
                        subtaskIndex={idx}
                        subtask={subtask}
                        onSplit={() => setShowSubtasksModal(false)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TaskCard;
